/**
 * ReferenceResolutionService
 * 
 * Resolves UUID references to human-readable names for:
 * - Manufacturers (manufacturers_v2 table)
 * - Standards (standards_v2 table)
 * - Other reference tables as needed
 */

import { SupabaseClient } from '@supabase/supabase-js'

/**
 * Resolved reference value with metadata
 */
export interface ResolvedReference {
  id: string
  displayValue: string
  type: 'manufacturer' | 'standard' | 'unknown'
  metadata?: Record<string, any>
}

/**
 * Result type for resolution operations
 */
export type ResolutionResult<T> = 
  | { success: true; data: T }
  | { success: false; error: string; fallback?: T }

/**
 * Service for resolving reference IDs to display values
 */
export class ReferenceResolutionService {
  private manufacturerCache = new Map<string, string>()
  private standardCache = new Map<string, { code: string; title: string }>()

  constructor(private readonly supabase: SupabaseClient) {}

  /**
   * Resolve a manufacturer UUID to its name
   */
  async resolveManufacturer(manufacturerId: string): Promise<ResolutionResult<string>> {
    // Check cache first
    if (this.manufacturerCache.has(manufacturerId)) {
      return {
        success: true,
        data: this.manufacturerCache.get(manufacturerId)!,
      }
    }

    try {
      const { data, error } = await this.supabase
        .from('manufacturers_v2')
        .select('name')
        .eq('id', manufacturerId)
        .single()

      if (error) {
        console.error('Error resolving manufacturer:', error)
        return {
          success: false,
          error: error.message,
          fallback: manufacturerId,
        }
      }

      if (!data) {
        return {
          success: false,
          error: 'Manufacturer not found',
          fallback: manufacturerId,
        }
      }

      // Cache the result
      this.manufacturerCache.set(manufacturerId, data.name)

      return {
        success: true,
        data: data.name,
      }
    } catch (error) {
      console.error('Exception resolving manufacturer:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        fallback: manufacturerId,
      }
    }
  }

  /**
   * Resolve a standard UUID to its code and/or title
   * 
   * @param standardId - UUID of the standard
   * @param codeOnly - If true, return only the code; if false, return "code - title"
   */
  async resolveStandard(
    standardId: string,
    codeOnly: boolean = false
  ): Promise<ResolutionResult<string>> {
    // Check cache first
    if (this.standardCache.has(standardId)) {
      const cached = this.standardCache.get(standardId)!
      return {
        success: true,
        data: codeOnly ? cached.code : `${cached.code} - ${cached.title}`,
      }
    }

    try {
      const { data, error } = await this.supabase
        .from('standards_v2')
        .select('code, title')
        .eq('id', standardId)
        .single()

      if (error) {
        console.error('Error resolving standard:', error)
        return {
          success: false,
          error: error.message,
          fallback: standardId,
        }
      }

      if (!data) {
        return {
          success: false,
          error: 'Standard not found',
          fallback: standardId,
        }
      }

      // Cache the result
      this.standardCache.set(standardId, { code: data.code, title: data.title })

      return {
        success: true,
        data: codeOnly ? data.code : `${data.code} - ${data.title}`,
      }
    } catch (error) {
      console.error('Exception resolving standard:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        fallback: standardId,
      }
    }
  }

  /**
   * Resolve multiple manufacturers in batch
   */
  async resolveManufacturers(
    manufacturerIds: string[]
  ): Promise<Map<string, string>> {
    const results = new Map<string, string>()
    
    // Separate cached and uncached IDs
    const uncached = manufacturerIds.filter(id => !this.manufacturerCache.has(id))
    
    // Add cached results
    manufacturerIds.forEach(id => {
      if (this.manufacturerCache.has(id)) {
        results.set(id, this.manufacturerCache.get(id)!)
      }
    })

    // Fetch uncached in batch
    if (uncached.length > 0) {
      try {
        const { data, error } = await this.supabase
          .from('manufacturers_v2')
          .select('id, name')
          .in('id', uncached)

        if (!error && data) {
          data.forEach(manufacturer => {
            this.manufacturerCache.set(manufacturer.id, manufacturer.name)
            results.set(manufacturer.id, manufacturer.name)
          })
        }
      } catch (error) {
        console.error('Error batch resolving manufacturers:', error)
      }
    }

    return results
  }

  /**
   * Resolve multiple standards in batch
   */
  async resolveStandards(
    standardIds: string[],
    codeOnly: boolean = false
  ): Promise<Map<string, string>> {
    const results = new Map<string, string>()
    
    // Separate cached and uncached IDs
    const uncached = standardIds.filter(id => !this.standardCache.has(id))
    const uncachedUuids = uncached.filter(id => ReferenceResolutionService.isUUID(id))
    const uncachedCodes = uncached.filter(id => !ReferenceResolutionService.isUUID(id))
    
    // Add cached results
    standardIds.forEach(id => {
      if (this.standardCache.has(id)) {
        const cached = this.standardCache.get(id)!
        results.set(id, codeOnly ? cached.code : `${cached.code} - ${cached.title}`)
      }
    })

    // Fetch uncached in batch
    if (uncachedUuids.length > 0) {
      try {
        const { data, error } = await this.supabase
          .from('standards_v2')
          .select('id, code, title')
          .in('id', uncachedUuids)

        if (!error && data) {
          data.forEach(standard => {
            this.standardCache.set(standard.id, { code: standard.code, title: standard.title })
            results.set(
              standard.id,
              codeOnly ? standard.code : `${standard.code} - ${standard.title}`
            )
          })
        }
      } catch (error) {
        console.error('Error batch resolving standards:', error)
      }
    }

    if (uncachedCodes.length > 0) {
      try {
        const { data, error } = await this.supabase
          .from('standards_v2')
          .select('id, code, title')
          .in('code', uncachedCodes)

        if (!error && data) {
          data.forEach(standard => {
            this.standardCache.set(standard.id, { code: standard.code, title: standard.title })
            this.standardCache.set(standard.code, { code: standard.code, title: standard.title })
            results.set(
              standard.code,
              codeOnly ? standard.code : `${standard.code} - ${standard.title}`
            )
          })
        }
      } catch (error) {
        console.error('Error batch resolving standards by code:', error)
      }
      uncachedCodes.forEach(code => {
        if (!results.has(code)) results.set(code, code)
      })
    }

    return results
  }

  /**
   * Detect reference type from spec_key and reference_table
   */
  detectReferenceType(
    specKey: string,
    referenceTable?: string
  ): 'manufacturer' | 'standard' | 'unknown' {
    // Check reference table first
    if (referenceTable === 'manufacturers_v2') return 'manufacturer'
    if (referenceTable === 'standards_v2') return 'standard'

    // Fallback to spec_key patterns
    const lowerKey = specKey.toLowerCase()
    
    if (lowerKey.includes('manufacturer') || lowerKey.includes('produsent')) {
      return 'manufacturer'
    }
    
    if (lowerKey.includes('standard') || lowerKey.includes('prod_acc_to')) {
      return 'standard'
    }

    return 'unknown'
  }

  /**
   * Auto-resolve a reference based on type detection
   */
  async resolveReference(
    referenceId: string,
    specKey: string,
    referenceTable?: string
  ): Promise<string> {
    const type = this.detectReferenceType(specKey, referenceTable)

    switch (type) {
      case 'manufacturer': {
        const result = await this.resolveManufacturer(referenceId)
        return result.success ? result.data : result.fallback || referenceId
      }
      
      case 'standard': {
        // For prod_acc_to, show only code; for others show code - title
        const codeOnly = specKey.toLowerCase() === 'prod_acc_to'
        const result = await this.resolveStandard(referenceId, codeOnly)
        return result.success ? result.data : result.fallback || referenceId
      }
      
      default:
        return referenceId
    }
  }

  /**
   * Check if a value looks like a UUID
   */
  static isUUID(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  }

  /**
   * Clear all caches (useful for testing or when data changes)
   */
  clearCache(): void {
    this.manufacturerCache.clear()
    this.standardCache.clear()
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    manufacturers: number
    standards: number
    total: number
  } {
    return {
      manufacturers: this.manufacturerCache.size,
      standards: this.standardCache.size,
      total: this.manufacturerCache.size + this.standardCache.size,
    }
  }
}
